import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const CertificateUploadModal = ({ employee, isOpen, onClose, onUpload }) => {
  const [uploadData, setUploadData] = useState({
    certificationType: '',
    provider: '',
    issueDate: '',
    expiryDate: '',
    certificateId: '',
    file: null
  });
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  if (!isOpen || !employee) return null;

  const certificateTypes = [
    'First Aid/CPR',
    'Safety Training',
    'Equipment Operation',
    'Hazmat Handling',
    'Fire Safety',
    'Workplace Violence Prevention',
    'Ergonomics Training',
    'Confined Space Entry',
    'Fall Protection',
    'Respiratory Protection'
  ];

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === "dragenter" || e?.type === "dragover") {
      setDragActive(true);
    } else if (e?.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);
    
    if (e?.dataTransfer?.files && e?.dataTransfer?.files?.[0]) {
      handleFileSelect(e?.dataTransfer?.files?.[0]);
    }
  };

  const handleFileSelect = (file) => {
    if (file && (file?.type === 'application/pdf' || file?.type?.startsWith('image/'))) {
      setUploadData(prev => ({ ...prev, file }));
      
      // Auto-extract data from filename if possible
      const filename = file?.name?.toLowerCase();
      if (filename?.includes('first aid') || filename?.includes('cpr')) {
        setUploadData(prev => ({ ...prev, certificationType: 'First Aid/CPR' }));
      } else if (filename?.includes('safety')) {
        setUploadData(prev => ({ ...prev, certificationType: 'Safety Training' }));
      }
    }
  };

  const handleInputChange = (field, value) => {
    setUploadData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsUploading(true);
    
    // Simulate upload progress
    for (let i = 0; i <= 100; i += 10) {
      setUploadProgress(i);
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    // Simulate blockchain anchoring
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const certificateData = {
      ...uploadData,
      employeeId: employee?.id,
      uploadDate: new Date()?.toISOString()?.split('T')?.[0],
      blockchainHash: `0x${Math.random()?.toString(16)?.substr(2, 64)}`,
      verified: true
    };
    
    onUpload(certificateData);
    setIsUploading(false);
    setUploadProgress(0);
    onClose();
    
    // Reset form
    setUploadData({
      certificationType: '',
      provider: '',
      issueDate: '',
      expiryDate: '',
      certificateId: '',
      file: null
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1200 p-4">
      <div className="bg-card rounded-lg border border-border w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Upload Certificate</h2>
            <p className="text-sm text-muted-foreground">
              Upload training certificate for {employee?.name}
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* File Upload Area */}
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-clinical ${
              dragActive ? 'border-primary bg-primary/5' : 'border-border'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {uploadData?.file ? (
              <div className="flex items-center justify-center space-x-3">
                <Icon name="FileText" size={24} className="text-primary" />
                <div>
                  <p className="font-medium text-foreground">{uploadData?.file?.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {(uploadData?.file?.size / 1024 / 1024)?.toFixed(2)} MB
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setUploadData(prev => ({ ...prev, file: null }))}
                >
                  <Icon name="X" size={16} />
                </Button>
              </div>
            ) : (
              <div>
                <Icon name="Upload" size={48} className="text-muted-foreground mx-auto mb-4" />
                <p className="text-lg font-medium text-foreground mb-2">
                  Drop certificate file here
                </p>
                <p className="text-sm text-muted-foreground mb-4">
                  or click to browse (PDF, JPG, PNG up to 10MB)
                </p>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => e?.target?.files?.[0] && handleFileSelect(e?.target?.files?.[0])}
                  className="hidden"
                  id="file-upload"
                />
                <Button type="button" variant="outline" onClick={() => document.getElementById('file-upload')?.click()}>
                  Browse Files
                </Button>
              </div>
            )}
          </div>

          {/* Certificate Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Certificate Type *
              </label>
              <select
                value={uploadData?.certificationType}
                onChange={(e) => handleInputChange('certificationType', e?.target?.value)}
                className="w-full px-3 py-2 bg-input border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                required
              >
                <option value="">Select certificate type</option>
                {certificateTypes?.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <Input
              label="Training Provider *"
              type="text"
              value={uploadData?.provider}
              onChange={(e) => handleInputChange('provider', e?.target?.value)}
              placeholder="e.g., Red Cross, OSHA"
              required
            />

            <Input
              label="Issue Date *"
              type="date"
              value={uploadData?.issueDate}
              onChange={(e) => handleInputChange('issueDate', e?.target?.value)}
              required
            />

            <Input
              label="Expiry Date *"
              type="date"
              value={uploadData?.expiryDate}
              onChange={(e) => handleInputChange('expiryDate', e?.target?.value)}
              required
            />

            <Input
              label="Certificate ID"
              type="text"
              value={uploadData?.certificateId}
              onChange={(e) => handleInputChange('certificateId', e?.target?.value)}
              placeholder="Certificate number or ID"
              className="md:col-span-2"
            />
          </div>

          {/* Upload Progress */}
          {isUploading && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-foreground">Uploading...</span>
                <span className="text-sm text-muted-foreground">{uploadProgress}%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              {uploadProgress === 100 && (
                <div className="flex items-center space-x-2 text-sm text-success">
                  <Icon name="Shield" size={16} />
                  <span>Anchoring to blockchain...</span>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
            <Button type="button" variant="outline" onClick={onClose} disabled={isUploading}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={!uploadData?.file || !uploadData?.certificationType || !uploadData?.provider || !uploadData?.issueDate || !uploadData?.expiryDate || isUploading}
              loading={isUploading}
            >
              Upload Certificate
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CertificateUploadModal;